import { createElement } from "react";
import { STEP_FRAME_CLASS } from "@/features/stepper/steptypes/step-shell";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import WebSmKeyHandoff from "@/features/stepper/steptypes/WebSmKeyHandoff_type";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import type { KeyQr } from "@/features/qr/types";

/** The backend's standard response wrapper. */
interface Envelope<T> {
  result: T;
}

/**
 * The visitor's own KEY — the code a scanner resolves back to their name.
 * Until it arrives (or if it fails) no code is cut: the plate keeps the clip's
 * placeholder rather than a scannable code that greets nobody.
 *
 * The session call mints or returns the member behind this browser, and the
 * KEY endpoint is idempotent per member, so this is safe to run on every
 * visit. `?qrData=` still wins, for pre-made invite links.
 */
function useMyKeyLink(): string | undefined {
  const { data } = useQuery({
    queryKey: ["key-qr", "self"],
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const { data: session } = await apiClient.post<
        Envelope<{ memberId: number }>
      >(API_ENDPOINTS.session.ensure, {});
      const memberId = session?.result?.memberId;
      if (!memberId) return null;
      const { data: key } = await apiClient.post<Envelope<KeyQr>>(
        API_ENDPOINTS.qr.key,
        { memberId },
      );
      // `qrData` is the SCAN destination (`<base>/code/<code>`, which greets
      // the scanner with this member's name). `link` is a different thing —
      // the page that shows the owner their own key — so encoding it sends
      // whoever scans straight back to a handoff screen.
      const target = key?.result?.qrData || key?.result?.link;
      if (!target) return null;
      // Always this site's own origin: a key shown on test.secretmissions.com
      // must invite people to test.secretmissions.com, not to whichever host
      // the backend wrote into the link (or a bare path).
      const url = new URL(target, window.location.origin);
      return `${window.location.origin}${url.pathname}${url.search}`;
    },
  });
  return data ?? undefined;
}

const str = (v: unknown) => (typeof v === "string" ? v : undefined);

export const Route = createFileRoute("/key-handoff")({
  // Fully configured by the invite link — no stepper graph, no journey logging.
  //   ?qrData=<text/url to encode as a real QR>   (preferred)
  //   ?qr=<url of a pre-made QR image>            (alternative)
  //   overlay tuning: &qrLeft=49%&qrTop=31.5%&qrSize=31%&qrRadius=9%
  validateSearch: (search: Record<string, unknown>) => ({
    qrData: str(search.qrData),
    qr: str(search.qr),
    qrLeft: str(search.qrLeft),
    qrTop: str(search.qrTop),
    qrSize: str(search.qrSize),
    qrRadius: str(search.qrRadius),
    qrPlate: str(search.qrPlate),
    original: str(search.original),
  }),
  component: KeyHandoffRoute,
});

/**
 * The key-handoff landing on its own route (e.g. where an invite link lands).
 * Standalone: it is NOT a stepper step — it never loads the graph or logs a
 * journey. Config comes from the URL. It is a display page: the key and its
 * code, no controls, nothing to dispatch.
 */
function KeyHandoffRoute() {
  const s = Route.useSearch();
  const myKey = useMyKeyLink();

  const map: Record<string, string | undefined> = {
    QrData: s.qrData ?? myKey,
    QrImage: s.qr,
    QrLeft: s.qrLeft,
    QrTop: s.qrTop,
    QrSize: s.qrSize,
    QrRadius: s.qrRadius,
    QrPlate: s.qrPlate,
    Original: s.original,
  };
  const getOpt = (name: string): string => map[name] ?? "";

  // in the same phone-shaped frame as every other step
  return (
    <div className={STEP_FRAME_CLASS}>
      {createElement(WebSmKeyHandoff, { getOpt, dispatch: () => {} })}
    </div>
  );
}
